import React from "react";
import { Card, CardContent, Typography, Box, Chip, IconButton, Tooltip, LinearProgress } from "@mui/material";
import { Delete as DeleteIcon, GetApp as DownloadIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Research } from "../types";
import { getStatusLabel, getStatusColor } from "../api";
import { Images } from "../../../assets";

interface ResearchCardProps {
  research: Research;
  onView: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  onDownload: (event: React.MouseEvent<HTMLElement>, uuid: string) => void;
  onSaveToTextFile: (uuid: string) => void;
}

const ResearchCard: React.FC<ResearchCardProps> = ({ research, onView, onDelete, onDownload, onSaveToTextFile }) => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) => theme.shadows[4],
        },
      }}
      onClick={() => onView(research.uuid)}
    >
      <CardContent>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" component="h3">
              {research.title || research.query}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {research.status === "completed" && (
                <>
                  <Tooltip title={t("deepResearch.tooltips.saveToTextFile")}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSaveToTextFile(research.uuid);
                      }}
                    >
                      <img src={Images.notesNebula.ico} alt="save" style={{ height: 24 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("deepResearch.tooltips.downloadReport")}>
                    <IconButton size="small" onClick={(e) => onDownload(e, research.uuid)}>
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {["completed", "error", "plan_pending"].includes(research.status) && (
                <Tooltip title={t("deepResearch.tooltips.deleteResearch")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(research.uuid);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={t(getStatusLabel(research.status))}
                color={
                  getStatusColor(research.status) as
                    | "default"
                    | "primary"
                    | "secondary"
                    | "error"
                    | "info"
                    | "success"
                    | "warning"
                }
                size="small"
              />
              {research.total_sources > 0 && (
                <Chip
                  label={t("deepResearch.list.sourcesCount", { count: research.total_sources })}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("deepResearch.dateLabels.created")}: {new Date(research.created_at).toLocaleString()}
              {research.completed_at && (
                <>
                  {" "}
                  | {t("deepResearch.dateLabels.completed")}: {new Date(research.completed_at).toLocaleString()}
                </>
              )}
            </Typography>
          </Box>
        </Box>

        {research.progress_percentage > 0 && !["completed", "error"].includes(research.status) && (
          <Box sx={{ my: 1 }}>
            <LinearProgress variant="determinate" value={research.progress_percentage} sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {t("deepResearch.dateLabels.progress")}: {research.progress_percentage}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ResearchCard;
